import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { 
  Search, X, User, Hash, Trophy, Lightbulb, 
  Calendar, FileText, Command, ArrowRight
} from 'lucide-react';
import { Input } from './ui/input';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';

const SEARCH_CATEGORIES = [
  { id: 'members', label: 'Membres', icon: User, color: 'text-blue-500' },
  { id: 'channels', label: 'Canaux', icon: Hash, color: 'text-green-500' },
  { id: 'victories', label: 'Victoires', icon: Trophy, color: 'text-yellow-500' },
  { id: 'ideas', label: 'Idées', icon: Lightbulb, color: 'text-primary' },
  { id: 'events', label: 'Événements', icon: Calendar, color: 'text-orange-500' },
  { id: 'resources', label: 'Ressources', icon: FileText, color: 'text-teal-500' },
];

const GlobalSearch = ({ isOpen, onClose }) => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Flatten results for keyboard navigation
  const flatResults = Object.entries(results).flatMap(([category, items]) => 
    (items || []).map(item => ({ ...item, category }))
  );

  // Keyboard shortcut to open
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (isOpen) {
          onClose();
        }
      }
      
      if (isOpen) {
        if (e.key === 'Escape') {
          onClose();
        } else if (e.key === 'ArrowDown') {
          e.preventDefault();
          setSelectedIndex(i => Math.min(i + 1, flatResults.length - 1));
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          setSelectedIndex(i => Math.max(i - 1, 0));
        } else if (e.key === 'Enter' && flatResults[selectedIndex]) {
          handleSelect(flatResults[selectedIndex]);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, flatResults, selectedIndex]);

  // Search function
  const performSearch = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) {
      setResults({});
      return;
    }

    setLoading(true);
    try {
      const response = await api.get(`/search?q=${encodeURIComponent(searchQuery)}`);
      setResults(response.data || {});
    } catch (error) {
      console.error('Search error:', error);
      setResults({});
    }
    setLoading(false);
  }, [api]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      performSearch(query);
    }, 300);
    return () => clearTimeout(timer);
  }, [query, performSearch]);

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setResults({});
      setSelectedIndex(0);
    }
  }, [isOpen]);

  const handleSelect = (item) => {
    onClose();
    
    switch (item.category) {
      case 'members':
        navigate(`/profile/${item.id}`);
        break;
      case 'channels':
        navigate(`/channels/${item.id}`);
        break;
      case 'victories':
        navigate(`/victories`);
        break;
      case 'ideas':
        navigate(`/ideas`);
        break;
      case 'events':
        navigate(`/calendar`);
        break;
      case 'resources':
        navigate(`/resources`);
        break;
      default:
        break;
    }
  };

  const getCategoryIcon = (categoryId) => {
    const cat = SEARCH_CATEGORIES.find(c => c.id === categoryId);
    return cat ? cat.icon : Search;
  };

  const getCategoryColor = (categoryId) => {
    const cat = SEARCH_CATEGORIES.find(c => c.id === categoryId);
    return cat ? cat.color : 'text-muted-foreground';
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-start justify-center pt-[10vh]"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, y: -20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -20, scale: 0.95 }}
          className="w-full max-w-2xl mx-4 bg-card rounded-2xl shadow-2xl border border-border overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Search Input */}
          <div className="flex items-center gap-3 p-4 border-b border-border">
            <Search className="w-5 h-5 text-muted-foreground" />
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher membres, canaux, victoires..."
              className="flex-1 border-0 bg-transparent text-lg focus-visible:ring-0 placeholder:text-muted-foreground/50"
              data-testid="global-search-input"
            />
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px]">ESC</kbd>
            </div>
          </div>

          {/* Results */}
          <div className="max-h-[60vh] overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center text-muted-foreground">
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                Recherche...
              </div>
            ) : query.length < 2 ? (
              <div className="p-6">
                <p className="text-sm text-muted-foreground mb-4">Raccourcis rapides</p>
                <div className="grid grid-cols-2 gap-2">
                  {SEARCH_CATEGORIES.map((cat) => {
                    const Icon = cat.icon;
                    return (
                      <button
                        key={cat.id}
                        onClick={() => setQuery(`@${cat.id} `)}
                        className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary/50 transition-colors text-left"
                      >
                        <Icon className={`w-5 h-5 ${cat.color}`} />
                        <span className="text-sm">{cat.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : flatResults.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                Aucun résultat pour "{query}"
              </div>
            ) : (
              <div className="p-2">
                {Object.entries(results).map(([category, items]) => {
                  if (!items || items.length === 0) return null;
                  const Icon = getCategoryIcon(category);
                  const color = getCategoryColor(category);
                  
                  return (
                    <div key={category} className="mb-4">
                      <p className={`text-xs font-medium ${color} px-3 py-1`}>
                        {SEARCH_CATEGORIES.find(c => c.id === category)?.label || category}
                      </p>
                      {items.slice(0, 5).map((item, idx) => {
                        const globalIdx = flatResults.findIndex(
                          r => r.id === item.id && r.category === category
                        );
                        const isSelected = globalIdx === selectedIndex;
                        
                        return (
                          <button
                            key={item.id}
                            onClick={() => handleSelect({ ...item, category })}
                            className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors text-left ${
                              isSelected ? 'bg-primary/20' : 'hover:bg-secondary/50'
                            }`}
                          >
                            {category === 'members' ? (
                              <Avatar className="h-8 w-8">
                                <AvatarImage src={item.avatar_url} />
                                <AvatarFallback className="text-xs">
                                  {item.first_name?.[0]}{item.last_name?.[0]}
                                </AvatarFallback>
                              </Avatar>
                            ) : (
                              <div className={`w-8 h-8 rounded-lg bg-secondary flex items-center justify-center`}>
                                <Icon className={`w-4 h-4 ${color}`} />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">
                                {item.title || item.name || `${item.first_name} ${item.last_name}`}
                              </p>
                              {item.description && (
                                <p className="text-xs text-muted-foreground truncate">
                                  {item.description}
                                </p>
                              )}
                            </div>
                            {isSelected && (
                              <ArrowRight className="w-4 h-4 text-primary" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-3 border-t border-border bg-secondary/30 text-xs text-muted-foreground">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-muted rounded">↑↓</kbd> naviguer
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-muted rounded">↵</kbd> sélectionner
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Command className="w-3 h-3" />
              <span>K pour ouvrir</span>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default GlobalSearch;
